/**
 * AJAX cart drawer for Majlis Mart. Single source of truth for quick-add, drawer rendering and cart badges.
 * Delivery is free on all UAE orders, so there is no free-shipping threshold or progress bar logic here.
 * Same-product quantity tiers: the discount itself is applied by the Shopify Function; this file only shows
 * the discount on each line, the total saved, and a "add 1 more to save X%" hint.
 */
(function () {
  'use strict';

  // Keep in sync with the discount function and with snippets/mm-tier-hint.liquid (ascending order).
  const TIERS = [
    { qty: 2, pct: 10 },
    { qty: 3, pct: 15 }
  ];

  const t = () => (window.theme && window.theme.strings) || {};
  const root = () => (window.MM && window.MM.root) || '/';
  const esc = (v) => (window.MM ? window.MM.escapeHtml(v) : String(v == null ? '' : v));
  const money = (c) => (window.MM ? window.MM.formatMoney(c) : (c / 100).toFixed(2));
  const isRTL = () => document.documentElement.dir === 'rtl';
  let lastTrigger = null;

  function sized(url, w) {
    if (!url) return '';
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }

  // Names of the discounts applied to one cart line (Shopify Function message or discount title).
  function discountTitles(item) {
    const allocations = item.line_level_discount_allocations || [];
    if (allocations.length) {
      return allocations.map((a) => a.discount_application && a.discount_application.title).filter(Boolean);
    }
    return (item.discounts || []).map((d) => d.title).filter(Boolean);
  }

  // "Add 1 more of this item to save 10%" - shown once per product, counting all of its lines together.
  function tierHint(item, productQty, firstKey) {
    if (firstKey[item.product_id] !== item.key) return '';
    const qty = productQty[item.product_id] || 0;
    const next = TIERS.find((tier) => qty < tier.qty);
    if (!next) return '';
    const need = next.qty - qty;
    const text = isRTL()
      ? (need === 1 ? 'أضف قطعة أخرى لتوفير ' : 'أضف ' + need + ' قطع أخرى لتوفير ') + next.pct + '%'
      : 'Add ' + need + ' more of this item to save ' + next.pct + '%';
    return `<p class="mm-tier-hint" style="margin:6px 0 0;padding:3px 10px;display:inline-block;border-radius:999px;background:#eef2ef;color:#0d4f3d;font-size:11px;font-weight:700;line-height:1.5">${esc(text)}</p>`;
  }

  window.CartDrawer = {
    open() {
      const drawer = document.querySelector('[data-cart-drawer]');
      if (!drawer) return;
      lastTrigger = document.activeElement;
      drawer.classList.add('is-active');
      drawer.removeAttribute('aria-hidden');
      document.body.classList.add('drawer-open');
      const close = drawer.querySelector('[data-cart-drawer-close]');
      close && close.focus();
    },

    close() {
      const drawer = document.querySelector('[data-cart-drawer]');
      if (!drawer || !drawer.classList.contains('is-active')) return;
      drawer.classList.remove('is-active');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('drawer-open');
      lastTrigger && lastTrigger.focus && lastTrigger.focus();
    },

    formatMoney: money,

    async fetchCart() {
      const res = await fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } });
      return res.json();
    },

    renderEmpty() {
      return `<div class="py-16 text-center">
        <p class="text-sm font-semibold text-stone-900">${esc(t().emptyCart || 'Your cart is empty')}</p>
        <a href="${esc((window.theme && window.theme.routes && window.theme.routes.allProductsCollectionUrl) || '/collections/all')}" class="mt-4 inline-flex items-center justify-center px-5 py-2.5 rounded-lg bg-stone-900 text-white text-xs font-semibold hover:bg-stone-800 transition-colors">${esc(t().startShopping || 'Start Shopping')}</a>
      </div>`;
    },

    async renderCartItems(cart) {
      const container = document.querySelector('[data-cart-items-container]');
      if (!container || !cart) return;
      if (!cart.items || cart.items.length === 0) { container.innerHTML = this.renderEmpty(); return; }
      const rm = isRTL() ? 'حذف' : 'Remove';
      const dec = isRTL() ? 'تقليل الكمية' : 'Decrease quantity';
      const inc = isRTL() ? 'زيادة الكمية' : 'Increase quantity';

      // Total quantity per product (all variants together), the same way the discount counts it.
      const productQty = {};
      const firstKey = {};
      cart.items.forEach((it) => {
        productQty[it.product_id] = (productQty[it.product_id] || 0) + it.quantity;
        if (!firstKey[it.product_id]) firstKey[it.product_id] = it.key;
      });

      container.innerHTML = cart.items.map((item, i) => {
        const line = i + 1;
        const variant = item.variant_title && item.variant_title !== 'Default Title' ? `<p class="text-xs text-stone-500 truncate mt-0.5">${esc(item.variant_title)}</p>` : '';
        const img = item.image ? `<img src="${esc(sized(item.image, 200))}" alt="${esc(item.product_title)}" width="80" height="80" loading="lazy" class="w-full h-full object-cover">` : '';
        const was = item.original_line_price > item.final_line_price
          ? `<s class="font-normal text-stone-400" style="margin-inline-start:6px">${money(item.original_line_price)}</s>`
          : '';
        const labels = discountTitles(item).map((title) => `<p class="text-[11px] text-emerald-700 mt-0.5">${esc(title)}</p>`).join('');
        const hint = tierHint(item, productQty, firstKey);
        return `<div class="cart-item flex gap-3.5 pb-4 border-b border-stone-100 last:border-b-0" data-line-item-key="${esc(item.key)}" data-line="${line}">
          <div class="w-20 h-20 rounded-xl bg-stone-100 overflow-hidden flex-shrink-0 border border-stone-200">${img}</div>
          <div class="flex-1 min-w-0 flex flex-col justify-between">
            <div>
              <h4 class="text-sm font-semibold text-stone-900 truncate"><a href="${esc(item.url)}">${esc(item.product_title)}</a></h4>
              ${variant}
              <div class="text-xs font-bold text-stone-900 mt-1">${money(item.final_line_price)}${was}</div>
              ${labels}
              ${hint}
            </div>
            <div class="flex items-center justify-between mt-2">
              <div class="flex items-center border border-stone-200 rounded-md bg-stone-50">
                <button type="button" class="w-7 h-7 flex items-center justify-center text-stone-600 hover:text-stone-900" data-drawer-qty-minus data-line="${line}" aria-label="${dec}"><svg class="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="5" y1="12" x2="19" y2="12"></line></svg></button>
                <span class="w-8 text-center text-xs font-bold text-stone-900" data-drawer-qty-val>${item.quantity}</span>
                <button type="button" class="w-7 h-7 flex items-center justify-center text-stone-600 hover:text-stone-900" data-drawer-qty-plus data-line="${line}" aria-label="${inc}"><svg class="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg></button>
              </div>
              <button type="button" class="text-stone-400 hover:text-rose-600 p-1" data-drawer-remove data-line="${line}" aria-label="${rm}"><svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg></button>
            </div>
          </div>
        </div>`;
      }).join('');
    },

    updateCartUI(cart) {
      document.querySelectorAll('[data-cart-count]').forEach((b) => {
        b.textContent = cart.item_count;
        if (b.classList.contains('cart-count-badge')) b.hidden = cart.item_count === 0;
      });
      document.querySelectorAll('[data-cart-subtotal]').forEach((s) => { s.textContent = money(cart.total_price); });

      const saved = Number(cart.total_discount) || 0;
      document.querySelectorAll('[data-cart-savings]').forEach((s) => { s.textContent = '-' + money(saved); });
      document.querySelectorAll('[data-cart-savings-row]').forEach((r) => { r.hidden = saved <= 0; });

      const footer = document.querySelector('[data-cart-drawer-footer]');
      if (footer) footer.hidden = cart.item_count === 0;
    },

    async refresh() {
      const cart = await this.fetchCart();
      await this.renderCartItems(cart);
      this.updateCartUI(cart);
      return cart;
    },

    async addItem(variantId, quantity = 1) {
      try {
        const res = await fetch(root() + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: variantId, quantity })
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.description || err.message || 'Add to cart failed');
        }
        await this.refresh();
        this.open();
        return true;
      } catch (err) {
        console.error('Cart add error:', err);
        window.MM && window.MM.toast(err.message && err.message !== 'Add to cart failed' ? err.message : (t().addError || 'Could not add this item to your cart.'));
        return false;
      }
    },

    async changeItem(line, quantity) {
      try {
        const res = await fetch(root() + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line, quantity })
        });
        if (!res.ok) throw new Error('Cart update failed');
        const cart = await res.json();
        await this.renderCartItems(cart);
        this.updateCartUI(cart);
      } catch (err) {
        console.error('Cart change error:', err);
        window.MM && window.MM.toast(t().addError || 'Could not update your cart.');
      }
    }
  };

  document.addEventListener('DOMContentLoaded', () => {
    const drawer = document.querySelector('[data-cart-drawer]');
    drawer && drawer.setAttribute('aria-hidden', 'true');

    document.addEventListener('click', async (e) => {
      const trigger = e.target.closest('[data-cart-drawer-trigger]');
      if (trigger) { e.preventDefault(); window.CartDrawer.open(); return; }
      if (e.target.closest('[data-cart-drawer-close], [data-cart-drawer-backdrop]')) { window.CartDrawer.close(); return; }

      const quick = e.target.closest('[data-quick-add]');
      if (quick) {
        e.preventDefault();
        if (quick.disabled || !quick.dataset.variantId) return;
        const original = quick.innerHTML;
        quick.disabled = true;
        quick.innerHTML = '<svg class="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10" stroke-opacity="0.25"></circle><path d="M12 2a10 10 0 0 1 10 10" stroke-linecap="round"></path></svg>';
        await window.CartDrawer.addItem(quick.dataset.variantId, 1);
        quick.innerHTML = original;
        quick.disabled = false;
        return;
      }

      const row = (el) => parseInt(el.dataset.line, 10);
      const qty = (el) => parseInt((el.parentElement.querySelector('[data-drawer-qty-val]') || {}).textContent || '1', 10);
      const minus = e.target.closest('[data-drawer-qty-minus]');
      if (minus) return window.CartDrawer.changeItem(row(minus), Math.max(0, qty(minus) - 1));
      const plus = e.target.closest('[data-drawer-qty-plus]');
      if (plus) return window.CartDrawer.changeItem(row(plus), qty(plus) + 1);
      const remove = e.target.closest('[data-drawer-remove]');
      if (remove) return window.CartDrawer.changeItem(row(remove), 0);
    });
  });
})();